import * as api from '../services/api';
import * as ubisoftServices from '../services/ubisoftServices';

import {
  Candidate,
  GamingHabit,
  PersonalInfo,
  Playtest
} from '../models/candidate';
import { EntitiesState, RootState } from '../models/state';
import { GameExperience, SurveyAnswer } from '../models/survey';
import { combineActions, createAction, handleActions } from 'redux-actions';
import { error, success } from '../utils/reducer';

import { ERROR_MESSAGE } from '../pages/root/RootLayout';
import { createLoadingSelector } from '../utils/selectors';
import { toast } from 'react-toastify';

// Actions
export const FETCH_ENUMS = 'entities/FETCH_ENUMS';
export const FETCH_ALL_COUNTRIES = 'entities/FETCH_ALL_COUNTRIES';
export const CHECK_USER = 'entities/CHECK_USER';
export const CREATE_CANDIDATE = 'entities/CREATE_CANDIDATE';
export const CHECK_USER_PROFILE = 'entities/CHECK_USER_PROFILE';
export const GET_USER = 'entities/GET_USER';
export const GET_CANDIDATE_PERSONALINFO = 'entities/GET_CANDIDATE_PERSONALINFO';
export const PUT_CANDIDATE_PERSONALINFO = 'entities/PUT_CANDIDATE_PERSONALINFO';
export const GET_CANDIDATE_PLAYTESTS = 'entities/GET_CANDIDATE_PLAYTESTS';
export const PUT_CANDIDATE_PLAYTESTS = 'entities/PUT_CANDIDATE_PLAYTESTS';
export const GET_CANDIDATE_GAMINGHABITS = 'entities/GET_CANDIDATE_GAMINGHABITS';
export const PUT_CANDIDATE_GAMINGHABITS = 'entities/PUT_CANDIDATE_GAMINGHABITS';
export const DELETE_CANDIDATE = 'entities/DELETE_CANDIDATE';
export const GET_CANDIDATE_GAMESPLAYED = 'entities/GET_CANDIDATE_GAMESPLAYED';
export const GET_GAMES = 'entities/GET_GAMES';
export const POST_CANDIDATE_GAME = 'entities/POST_CANDIDATE_GAME';
export const PUT_CANDIDATE_GAME = 'entities/PUT_CANDIDATE_GAME';
export const DELETE_CANDIDATE_GAME = 'entities/DELETE_CANDIDATE_GAME';
export const GET_SURVEY = 'entities/GET_SURVEY';
export const GET_SURVEY_ANSWERS = 'entities/GET_SURVEY_ANSWERS';
export const POST_SURVEY = 'entities/POST_SURVEY';
export const POST_SESSION_PARTICIPATION = 'entities/POST_SESSION_PARTICIPATION';

// Action creators
export const fetchEnums = createAction(FETCH_ENUMS, async () =>
  api.getEnums().catch(() => toast.error(ERROR_MESSAGE))
);

export const fetchAllCountries = createAction(FETCH_ALL_COUNTRIES, async () =>
  api.getAllCountries().catch(() => toast.error(ERROR_MESSAGE))
);

export const checkUser = createAction(CHECK_USER, async () =>
  api.getExists().catch(() => toast.error(ERROR_MESSAGE))
);

export const createCandidate = createAction(
  CREATE_CANDIDATE,
  async (data: Candidate) => api.postCandidate(data)
);

export const checkUserStatus = createAction(CHECK_USER_PROFILE, async () =>
  api.getUserStatus().catch(() => toast.error(ERROR_MESSAGE))
);

export const getUser = createAction(GET_USER, async (id: string) =>
  ubisoftServices.getUser(id)
);

export const getCandidatePersonalInfo = createAction(
  GET_CANDIDATE_PERSONALINFO,
  async () =>
    api.getCandidatePersonalInfo().catch(() => toast.error(ERROR_MESSAGE))
);

export const putCandidatePersonalInfo = createAction(
  PUT_CANDIDATE_PERSONALINFO,
  async (data: PersonalInfo) => api.putCandidatePersonalInfo(data)
);

export const getCandidatePlaytests = createAction(
  GET_CANDIDATE_PLAYTESTS,
  async () =>
    api.getCandidatePlaytests().catch(() => toast.error(ERROR_MESSAGE))
);

export const putCandidatePlaytests = createAction(
  PUT_CANDIDATE_PLAYTESTS,
  async (data: Playtest) => api.putCandidatePlaytests(data)
);

export const getCandidateGamingHabits = createAction(
  GET_CANDIDATE_GAMINGHABITS,
  async () =>
    api.getCandidateGamingHabits().catch(() => toast.error(ERROR_MESSAGE))
);

export const putCandidateGamingHabits = createAction(
  PUT_CANDIDATE_GAMINGHABITS,
  async (data: GamingHabit) => api.putCandidateGamingHabits(data)
);

export const deleteCandidate = createAction(DELETE_CANDIDATE, async () =>
  api.deleteCandidate()
);

export const getCandidateGameExperiencesPlayed = createAction(
  GET_CANDIDATE_GAMESPLAYED,
  async () =>
    api
      .getCandidateGameExperiencesPlayed()
      .catch(() => toast.error(ERROR_MESSAGE))
);

export const getGames = createAction(GET_GAMES, async (data: string) =>
  api.getGames(data).catch(() => toast.error(ERROR_MESSAGE))
);

export const postCandidateGame = createAction(
  POST_CANDIDATE_GAME,
  async (data: GameExperience) => api.postGameExperience(data)
);

export const putCandidateGame = createAction(
  PUT_CANDIDATE_GAME,
  async (data: GameExperience, id: string) => api.putGameExperience(data, id)
);

export const deleteCandidateGame = createAction(
  DELETE_CANDIDATE_GAME,
  async (id: string) => api.deleteGameExperience(id)
);

export const getSurvey = createAction(
  GET_SURVEY,
  async (id: string, isPreview: boolean | undefined) =>
    api.getSurvey(id, isPreview)
);

export const getSurveyAnswer = createAction(
  GET_SURVEY_ANSWERS,
  async (id: string) =>
    api.getSurveyAnswer(id).catch(() => toast.error(ERROR_MESSAGE))
);

export const postSurvey = createAction(
  POST_SURVEY,
  async (survey: SurveyAnswer, id: string) => api.postSurvey(survey, id)
);

export const postSessionParticipation = createAction(
  POST_SESSION_PARTICIPATION,
  async (id: string) => api.postSessionParticipation(id)
);

// Default state
const defaultState: EntitiesState = {
  userStatus: {},
  candidate: {}
};

// Reducer
const reducer = handleActions(
  {
    [success(FETCH_ENUMS)]: (state, { payload }: any) => ({
      ...state,
      enums: payload
    }),
    [success(FETCH_ALL_COUNTRIES)]: (state, { payload }: any) => ({
      ...state,
      allCountries: payload
    }),
    [success(CHECK_USER)]: (state, { payload }: any) => ({
      ...state,
      userExists: payload
    }),
    [success(CHECK_USER_PROFILE)]: (state, { payload }: any) => ({
      ...state,
      userStatus: payload
    }),
    [success(GET_USER)]: (state, { payload }: any) => ({
      ...state,
      ubisoftUser: payload
    }),
    [combineActions(
      ...success([GET_CANDIDATE_PERSONALINFO, PUT_CANDIDATE_PERSONALINFO])
    ) as any]: (state, { payload }: any) => ({
      ...state,
      candidate: {
        ...state.candidate,
        personalInfo: payload
      }
    }),
    [combineActions(
      ...success([GET_CANDIDATE_PLAYTESTS, PUT_CANDIDATE_PLAYTESTS])
    ) as any]: (state, { payload }: any) => ({
      ...state,
      candidate: {
        ...state.candidate,
        playtests: payload
      }
    }),
    [combineActions(
      ...success([GET_CANDIDATE_GAMINGHABITS, PUT_CANDIDATE_GAMINGHABITS])
    ) as any]: (state, { payload }: any) => ({
      ...state,
      candidate: {
        ...state.candidate,
        gamingHabits: payload
      }
    }),
    [success(DELETE_CANDIDATE)]: () => ({
      ...defaultState
    }),
    [success(GET_CANDIDATE_GAMESPLAYED)]: (state, { payload }: any) => ({
      ...state,
      candidate: {
        ...state.candidate,
        gamesPlayed: payload.filter((game: GameExperience) => game.played)
      }
    }),
    [success(GET_GAMES)]: (state, { payload }: any) => ({
      ...state,
      games: payload
    }),
    [success(GET_SURVEY)]: (state, { payload }: any) => ({
      ...state,
      surveyStatus: payload.status,
      survey: payload.data
    }),
    [error(GET_SURVEY)]: (state, { payload }: any) => ({
      ...state,
      surveyStatus: payload.response.status,
      survey: {}
    }),
    [success(GET_SURVEY_ANSWERS)]: (state, { payload }: any) => ({
      ...state,
      surveyAnswer: payload
    }),
    [error(POST_SESSION_PARTICIPATION)]: (state) => ({
      ...state,
      hasErrorPostSessionParticipation: true
    })
  },
  defaultState
);

// Selectors
export const selectUserExists = (state: RootState) => state.entities.userExists;
export const selectUserStatus = (state: RootState) => state.entities.userStatus;
export const selectIsFetchingUserExistes = createLoadingSelector([CHECK_USER]);
export const selectUbisoftUser = (state: RootState) =>
  state.entities.ubisoftUser;
export const selectPersonalInfo = (state: RootState) =>
  state.entities.candidate.personalInfo;
export const selectPlaytests = (state: RootState) =>
  state.entities.candidate.playtests;
export const selectGamingHabits = (state: RootState) =>
  state.entities.candidate.gamingHabits;
export const selectIsFetchingUbisoftUser = createLoadingSelector([GET_USER]);
export const selectIsFetchingPersonalInfo = createLoadingSelector([
  GET_CANDIDATE_PERSONALINFO
]);
export const selectIsFetchingPlaytests = createLoadingSelector([
  GET_CANDIDATE_PLAYTESTS
]);
export const selectIsFetchingGamingHabits = createLoadingSelector([
  GET_CANDIDATE_GAMINGHABITS
]);
export const selectEnums = (state: RootState) => state.entities.enums;
export const selectAllCountries = (state: RootState) =>
  state.entities.allCountries;
export const selectIsFetchingEnums = createLoadingSelector([FETCH_ENUMS]);
export const selectIsFetchingAllCountries = createLoadingSelector([
  FETCH_ALL_COUNTRIES
]);
export const selectIsDeletingCandidate = createLoadingSelector([
  DELETE_CANDIDATE
]);
export const selectGamesPlayed = (state: RootState) =>
  state.entities.candidate.gamesPlayed;
export const selectIsFetchingGamesPlayed = createLoadingSelector([
  GET_CANDIDATE_GAMESPLAYED
]);
export const selectSearchedGames = (state: RootState) => state.entities.games;
export const selectIsFetchingGames = createLoadingSelector([GET_GAMES]);
export const selectIsDeletingCandidateGame = createLoadingSelector([
  DELETE_CANDIDATE_GAME
]);
export const selectSurveyStatus = (state: RootState) =>
  state.entities.surveyStatus;
export const selectSurvey = (state: RootState) => state.entities.survey;
export const selectIsFetchingSurvey = createLoadingSelector([GET_SURVEY]);
export const selectSurveyAnswer = (state: RootState) =>
  state.entities.surveyAnswer;
export const selectIsFetchingSurveyAnswer = createLoadingSelector([
  GET_SURVEY_ANSWERS
]);
export const selectIsPostingSessionParticipation = createLoadingSelector([
  POST_SESSION_PARTICIPATION
]);
export const selectHasErrorPostSessionParticipation = (state: RootState) =>
  state.entities.hasErrorPostSessionParticipation;

export default reducer;
